import { PublishAlertDialog } from '@/components/AlertDialog/PresetAlerts/PublishAlertDialog'
import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { CreateDetailsDialog } from '@/components/CreateDetailsDialog'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { PublishStatusBanner } from '@/components/PublishStatusBanner'
import { usePublishStatus } from '@/hooks/queries/useBcpPublishStatus'
import {
  launchesExportQueryOptions,
  launchesSearchSchema,
  useCleanInactiveErpsInLaunches,
  useCreateLaunch,
  useLaunches,
  usePublishLaunches,
} from '@/hooks/queries/useLaunches'
import { queryClient } from '@/main'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { Button } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { HTTPException } from 'hono/http-exception'
import { Loader2Icon, Trash2Icon, UploadCloudIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as launchDetailsRoute } from './$launchId'
import LaunchesDataTable from './-components/LaunchesDataTable'

export const Route = createFileRoute('/_auth/_modules/_app/assortment/launches/')({
  component: Launches,
  validateSearch: (search) => launchesSearchSchema.parse(search),
})

function Launches() {
  const { t } = useTranslation(['launches', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { data: launches } = useLaunches({ ...search })
  const { isPending: isPublishPending, mutate: publishLaunches } = usePublishLaunches()
  const { isPending: isCleanPending, mutate: cleanInactiveErpsInLaunches } =
    useCleanInactiveErpsInLaunches(search)
  const { isPublishing: isPublishActive } = usePublishStatus('launches')

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)

  const {
    isPending: isCreateLaunchPending,
    mutate: createLaunch,
    error: createLaunchError,
    reset: resetCreateLaunch,
  } = useCreateLaunch({
    queries: search,
    handleOnSuccess: (launchId) => {
      setIsCreateOpen(false)
      navigate({
        to: launchDetailsRoute.to,
        params: { launchId: String(launchId) },
      })
    },
  })

  const getCsvExport = () => queryClient.ensureQueryData(launchesExportQueryOptions(search))

  return (
    <PageContainer>
      <PublishStatusBanner publishType="launches" />
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.LAUNCHES}
        title={t('tutorials.launches.dialog.title')}
        guideUrl="/products/launches/"
      >
        {t('tutorials.launches.dialog.description')}
      </IntroductionDialog>

      <Spread>
        <PageHeader>{t('launchesPage.header')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton getCsvExport={getCsvExport} rowCount={launches?.rowCount ?? 0} />

          <PermissionsGuard required={['assortment_launches_create']}>
            <CreateDetailsDialog
              isOpen={isCreateOpen}
              onOpenChange={(open) => {
                setIsCreateOpen(open)
                if (!open) resetCreateLaunch()
              }}
              triggerVariant="secondary"
              triggerDisabled={isPublishActive}
              triggerLabel={t('launchesPage.createLaunchButton')}
              title={t('createLaunchDialog.title')}
              nameLabel={t('createLaunchDialog.launchName')}
              namePlaceholder={t('createLaunchDialog.launchNamePlaceholder')}
              descriptionLabel={t('createLaunchDialog.launchDescription')}
              descriptionPlaceholder={t('createLaunchDialog.launchDescriptionPlaceholder')}
              isDescriptionRequired
              errorMessage={
                createLaunchError && (createLaunchError as HTTPException).status === 400
                  ? createLaunchError.message
                  : undefined
              }
              isPending={isCreateLaunchPending}
              onSubmit={({ name, description }) =>
                createLaunch({ launchName: name, launchDescr: description })
              }
            />
          </PermissionsGuard>

          <PermissionsGuard required={['assortment_launches_edit']}>
            <Button
              variant="secondary"
              onClick={() => cleanInactiveErpsInLaunches()}
              disabled={isCleanPending || isPublishActive}
            >
              {isCleanPending ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <Trash2Icon className="size-4" />
              )}
              {t('launchesPage.cleanInactiveErpsButton')}
            </Button>
          </PermissionsGuard>

          <PermissionsGuard required={['assortment_launches_publish']}>
            <Button onClick={() => setPublishOpen(true)} disabled={isPublishActive}>
              <UploadCloudIcon /> {t('actions.publish', { ns: 'common' })}
            </Button>

            <PublishAlertDialog
              title={t('launchesPage.publishDialog.title')}
              warning={t('launchesPage.publishDialog.warning')}
              editBlockWarning={t('publish.editBlockWarning', { ns: 'common' })}
              open={publishOpen}
              onOpenChange={setPublishOpen}
              onPublish={publishLaunches}
              isPending={isPublishPending}
              guideUrl="/products/publishing-changes/publish-launches"
            />
          </PermissionsGuard>
        </HStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <LaunchesDataTable isPublishing={isPublishActive} />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
